import React from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Spin,
  Statistic,
  Switch,
  Tooltip,
  message,
} from 'antd';
import { DownloadOutlined, LoadingOutlined, PlusCircleFilled } from '@ant-design/icons';
import CurrencyFormat from 'react-currency-format';
import * as reactstrp from 'reactstrap';
import ReactExport from '@ibrahimrahmani/react-export-excel';
import * as serverconfig from '../serverconn';
import { getDefaultJournalHealthDateRange, periodLabel } from '../journalHealthUtils';

var CryptoJS = require('crypto-js');

const FormItem = Form.Item;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const dateFormat = 'DD/MM/YYYY';
const antIcon = <LoadingOutlined style={{ fontSize: 60 }} spin />;

const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

var sacco = '';

class TransactionsMissingJournalsView extends React.Component {
  state = {
    dateone: '',
    datetwo: '',
    datarequested: false,
    sheetitems: [],
    companyprofile: {},
    searched: false,
    totalMissing: 0,
  };

  componentDidMount() {
    if (localStorage.getItem('sacco')) {
      sacco = CryptoJS.AES.decrypt(localStorage.getItem('sacco'), 'my-secret-key@12345').toString(CryptoJS.enc.Utf8);
    } else {
      sacco = '';
    }

    if (localStorage.getItem('token')) {
      axios.defaults.headers = {
        'Content-Type': 'application/json',
        Authorization: `Token ${localStorage.getItem('token')}`,
      };
    }

    axios.get(`${serverconfig.backendserverurl}/api/saccos/${sacco}`)
      .then((res) => {
        this.setState({ companyprofile: res.data });
        const navState = this.props.location && this.props.location.state;
        if (navState && navState.dateone && navState.datetwo) {
          this.setState(
            { dateone: navState.dateone, datetwo: navState.datetwo },
            () => this.loadReport()
          );
        } else {
          const defaults = getDefaultJournalHealthDateRange(res.data);
          this.setState(defaults);
        }
      })
      .catch(() => {
        const navState = this.props.location && this.props.location.state;
        if (navState && navState.dateone && navState.datetwo) {
          this.setState(
            { dateone: navState.dateone, datetwo: navState.datetwo },
            () => this.loadReport()
          );
        }
      });
  }

  handledatechange = (date, dateString) => {
    this.setState({ dateone: dateString[0], datetwo: dateString[1] });
  };

  applyCurrentFyRange = () => {
    const defaults = getDefaultJournalHealthDateRange(this.state.companyprofile);
    this.setState(defaults);
  };

  loadReport = () => {
    const form_data = new FormData();
    form_data.append('sacco', sacco);
    form_data.append('dateone', this.state.dateone);
    form_data.append('datetwo', this.state.datetwo);

    this.setState({ datarequested: true });
    axios.post(
      `${serverconfig.backendserverurl}/customqueries/get_transactions_missing_journals`,
      form_data,
      { headers: { 'content-type': 'multipart/form-data' }, timeout: 120000 }
    )
      .then((res) => {
        const items = JSON.parse(res.data.report || '[]');
        this.setState({
          datarequested: false,
          sheetitems: items,
          searched: true,
          totalMissing: res.data.total_missing_journals || items.length,
        });
        if ((res.data.total_missing_journals || 0) === 0) {
          message.success('No missing GL journals found for the selected period');
        } else {
          message.warning(`Found ${res.data.total_missing_journals} transaction(s) without GL journals`);
        }
      })
      .catch((err) => {
        console.log(err);
        this.setState({ datarequested: false, searched: true });
        message.error('Failed to load missing journals report');
      });
  };

  render() {
    const rangeValue =
      this.state.dateone && this.state.datetwo
        ? [moment(this.state.dateone, dateFormat), moment(this.state.datetwo, dateFormat)]
        : null;

    return (
      <div>
        <Card bordered={false} style={{ marginBottom: 16 }}>
          <h3 style={{ marginBottom: 4, color: '#154360', fontWeight: 700 }}>Missing GL journals</h3>
          <p style={{ marginBottom: 0, color: '#667085' }}>
            Member subaccount transactions that posted without matching GeneralJournal lines — often caused by network interruptions during save.
          </p>
        </Card>

        <Collapse defaultActiveKey={['1']}>
          <Panel header="Search member transactions with no general journal entries" key="1">
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Tip"
              description="Use “Current FY” to scan from the day after your data entry cap through today — the same window used for login alerts."
            />

            <Form layout="inline">
              <FormItem label="Date range">
                <RangePicker
                  value={rangeValue}
                  onChange={this.handledatechange}
                  format={dateFormat}
                />
              </FormItem>
              <FormItem>
                <Button onClick={this.applyCurrentFyRange}>Current FY</Button>
              </FormItem>
              <FormItem>
                <Button
                  type="primary"
                  loading={this.state.datarequested}
                  onClick={() => {
                    if (!this.state.dateone || !this.state.datetwo) {
                      message.error('Please select a date range');
                    } else {
                      this.loadReport();
                    }
                  }}
                >
                  Search
                </Button>
              </FormItem>
              <FormItem>
                <ExcelFile
                  filename="Member transactions missing journals"
                  element={<Button type="default" icon={<DownloadOutlined />} disabled={!this.state.sheetitems.length}>Download Excel</Button>}
                >
                  <ExcelSheet data={this.state.sheetitems} name="Missing journals">
                    <ExcelColumn label="No" value="item_no" />
                    <ExcelColumn label="Date" value="date" />
                    <ExcelColumn label="Account Name" value="FullName" />
                    <ExcelColumn label="Account No" value="AccountNumber" />
                    <ExcelColumn label="Details" value="transaction_details" />
                    <ExcelColumn label="Sub account" value="subaccount" />
                    <ExcelColumn label="Debit" value="debit" />
                    <ExcelColumn label="Credit" value="credit" />
                    <ExcelColumn label="Journal No" value="journal_no" />
                    <ExcelColumn label="User" value="user" />
                  </ExcelSheet>
                </ExcelFile>
              </FormItem>
            </Form>

            {this.state.searched ? (
              <>
                <Divider />
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col xs={24} sm={12} md={8}>
                    <Card size="small">
                      <Statistic title="Period" value={periodLabel(this.state.dateone, this.state.datetwo)} valueStyle={{ fontSize: 16 }} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card size="small">
                      <Statistic
                        title="Transactions missing GL"
                        value={this.state.totalMissing}
                        valueStyle={{ color: this.state.totalMissing > 0 ? '#cf1322' : '#389e0d' }}
                      />
                    </Card>
                  </Col>
                </Row>
              </>
            ) : null}

            <Spin spinning={this.state.datarequested} indicator={antIcon}>
              {this.state.searched && this.state.sheetitems.length === 0 ? (
                <Empty description="No missing GL journals in this period" />
              ) : (
                <reactstrp.Table bordered responsive>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>Date</th>
                      <th>Account name</th>
                      <th>Account no</th>
                      <th>Details</th>
                      <th>Sub account</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Journal no</th>
                      <th>User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.state.sheetitems.map((item) => (
                      <tr key={item.id}>
                        <td>{item.item_no}</td>
                        <td>{item.date}</td>
                        <td>{item.FullName}</td>
                        <td>{item.AccountNumber}</td>
                        <td>{item.transaction_details}</td>
                        <td>{item.subaccount}</td>
                        <td className="align-right">
                          <CurrencyFormat value={item.debit} displayType="text" thousandSeparator />
                        </td>
                        <td className="align-right">
                          <CurrencyFormat value={item.credit} displayType="text" thousandSeparator />
                        </td>
                        <td>{item.journal_no}</td>
                        <td>{item.user}</td>
                      </tr>
                    ))}
                  </tbody>
                </reactstrp.Table>
              )}
            </Spin>
          </Panel>
        </Collapse>
      </div>
    );
  }
}

export default TransactionsMissingJournalsView;
